'use client';

import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-hot-toast';
import * as Sentry from '@sentry/react';

export const getToken = () => {
  return Cookies.get('access_token');
};

export const setToken = access_token => {
  return Cookies.set('access_token', access_token);
};

export const removeToken = () => {
  Cookies.remove('access_token');
};

// Axios instance configuration
export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_API_URL,
  headers: {
    'Content-Type': 'application/json',
    // Identify this app so the API scopes its httpOnly refresh-token cookie per
    // client. The admin and instructor apps share one API host, so without this
    // they'd overwrite each other's refresh cookie and swap sessions.
    'X-Client-Type': 'admin',
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(
  async config => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

const getErrorMessage = error => {
  if (error.response?.data?.detail) {
    if (typeof error.response.data.detail === 'string') {
      return error.response.data.detail;
    }
    if (Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(err => err.msg || err).join(', ');
    }
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

const getUserFriendlyMessage = (status, errorMessage) => {
  switch (status) {
    case 400:
      return errorMessage || 'Invalid request. Please check your input.';
    case 401:
      return 'Your session has expired. Please login again.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'The requested resource was not found.';
    case 422:
      return errorMessage || 'Validation error. Please check your input.';
    case 500:
      return 'Server error. Our team has been notified. Please try again later.';
    case 502:
      return 'Service temporarily unavailable. Please try again.';
    case 503:
      return 'Service is under maintenance. Please try again later.';
    default:
      return errorMessage || 'An unexpected error occurred. Please try again.';
  }
};

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Only 401 (expired/invalid token) warrants a refresh + retry. A 403 means
    // the user is authenticated but lacks permission — refreshing the token
    // never helps and the retry just fires the forbidden request twice.
    if (status === 401 && !originalRequest._retry) {
      const token = getToken();
      if (!token) {
        return Promise.reject(error);
      }

      const currentPath = window.location.pathname;
      const isAuthPage =
        currentPath === '/' ||
        currentPath === '/login' ||
        currentPath === '/forgot-password' ||
        currentPath === '/reset-password' ||
        currentPath === '/verify-otp';

      if (isAuthPage) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const newToken = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        toast.error('Session expired. Please login again.');
        removeToken();
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return Promise.reject(refreshError);
      }
    }

    if (error.code === 'ERR_CANCELED' || error.message?.includes('cancel')) {
      return Promise.reject(error);
    }

    if (status === 400 || status === 404) {
      return Promise.reject(error);
    }

    if (!originalRequest._skipErrorToast) {
      const errorMessage = getErrorMessage(error);
      const userMessage = getUserFriendlyMessage(status, errorMessage);
      toast.error(userMessage);
    }

    if (status >= 500 || !status) {
      Sentry.captureException(error, {
        tags: {
          type: 'api_error',
          status: status || 'network_error',
        },
        extra: {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status: status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          requestData: originalRequest?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

async function refreshToken() {
  const token = getToken();
  if (!token) {
    throw new Error('No token available for refresh');
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_API_URL}/auth/jwt/refresh`,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Type': 'admin',
        },
      }
    );
    const newToken = response?.data?.access_token;
    if (newToken) {
      setToken(newToken);
      return newToken;
    }
    throw new Error('No token received from refresh endpoint');
  } catch (error) {
    console.error('Error refreshing token', error);
    throw error;
  }
}
